// Linked runs settings for the active business (mig 20261288000000):
// packhouse_config.linked_runs and each commodity's run_grain
// (tenant_commodities). A failed read is an error, never a quiet "off"; a
// business with no packhouse_config row (a farm) reads as off.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import type { RunGrain } from '@/lib/linkedRuns'

export const LINKED_RUNS_KEY = 'linked-runs'

export function useLinkedRunsOn() {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: [LINKED_RUNS_KEY, 'on', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('packhouse_config')
        .select('linked_runs')
        .eq('tenant_id', tenantId!)
        .maybeSingle()
      if (error) throw error
      return !!(data as { linked_runs?: boolean } | null)?.linked_runs
    },
  })
}

export interface CommodityGrain {
  commodity_id: string
  name: string
  run_grain: RunGrain
}

/** This business's commodities with their run choice. */
export function useRunGrains() {
  const tenantId = useActiveTenantId()
  return useQuery({
    queryKey: [LINKED_RUNS_KEY, 'grains', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('tenant_commodities')
        .select('commodity_id, run_grain, commodities:commodity_id(name)')
        .eq('tenant_id', tenantId!)
      if (error) throw error
      return ((data ?? []) as unknown as { commodity_id: string; run_grain: RunGrain | null; commodities: { name: string } | null }[])
        .map(r => ({ commodity_id: r.commodity_id, name: r.commodities?.name ?? '', run_grain: r.run_grain ?? 'VARIETY' }))
        .sort((a, b) => a.name.localeCompare(b.name)) as CommodityGrain[]
    },
  })
}

/** The run choice for a commodity name (runs.commodity); per variety when the
 *  commodity is not listed or the read has not landed. */
export function grainFor(grains: CommodityGrain[] | undefined, commodity: string | null | undefined): RunGrain {
  if (!commodity) return 'VARIETY'
  const c = commodity.trim().toUpperCase()
  return grains?.find(g => g.name.trim().toUpperCase() === c)?.run_grain ?? 'VARIETY'
}
